<script lang="ts">
    // "Sign In" form.
    import { onDestroy } from 'svelte'
    import { writable, type Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import * as yup from 'yup'

    import { lg } from 'hydria/services/language'

    import { params } from './globalContext'

    // Clear the error after some time
    const CLEAR_ERROR_AFTER_MS = 7500
    const MIN_PASSWORD_LENGTH = 15

    // Bunch of functions to call on destroy, like store subscriptions.
    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const schema = yup.object().shape({
        password: yup.string().required().min(MIN_PASSWORD_LENGTH),
        pconfirm: yup
            .string()
            .required()
            .test({
                name: 'equals',
                exclusive: false,
                message: '',
                test: function (value) {
                    return value === fields.password
                },
            }),
    })

    interface IFields {
        password: string
        pconfirm: string
    }
    const initialFields: IFields = { password: '', pconfirm: '' }
    const fields: IFields = $state(Object.assign({}, initialFields))
    const password = writable(fields.password)
    $effect(() => {
        $password = fields.password
    })

    // When true we are signing in, (e.g. show a spinner)
    const communicating = writable(false)
    const success = writable(false)

    // When not null, there was an error to show.
    // That error is shown for a few seconds or until a value changes.
    const error: Writable<undefined | string> = writable(undefined)
    let errorTimer: ReturnType<typeof setTimeout> | undefined = undefined

    function clearErrorTimer() {
        if (errorTimer) {
            clearTimeout(errorTimer)
            errorTimer = undefined
        }
    }
    function clearError() {
        if ($error != null) {
            clearErrorTimer()
            error.set(undefined)
        }
    }

    unsubs.push(
        error.subscribe((newError) => {
            // This is called when the `error` store changes
            clearErrorTimer()
            if (newError != null) {
                errorTimer = setTimeout(() => {
                    errorTimer = undefined
                    clearError()
                }, CLEAR_ERROR_AFTER_MS)
            }
        }),
    )

    // Set 'isvalid' variables based on their validity when a value changes
    // @ts-expect-error
    let passwordIsValid = $derived(schema.fields.password.isValidSync(fields.password))
    // @ts-expect-error
    let pconfirmIsValid = $derived(schema.fields.pconfirm.isValidSync(fields.pconfirm))
    let formIsValid = $derived(schema.isValidSync(fields))

    // We setup a store so we can easily do stuff when anything changes in the fields.
    // The purpose being to clear the error if there’s one.
    let formData = writable(fields)
    $effect(() => {
        $formData = fields
    })
    unsubs.push(
        formData.subscribe((nv) => {
            if (errorTimer != null) {
                clearError()
            }
        }),
    )

    async function setPassword(token, password) {
        let fdata = new Blob([JSON.stringify({ new_password: password }, null, 0)], { type: 'application/json' })
        const headers = new Headers()
        headers.append('Accept', 'application/json')
        headers.append('X-Lg', $lg)

        const r = await fetch(new Request(`/json/session/1/request/${token}`), {
            method: 'POST',
            headers: headers,
            body: fdata,
        })

        let data: any
        try {
            data = await r.json()
        } catch {}

        if (r.status != 200) {
            throw { success: false, error: data && data.error ? data.error : null }
        }

        if (data.success) {
            return { success: true }
        }
        throw { success: false, error: data && data.error ? data.error : null }
    }

    async function formSubmit(event: Event) {
        event.preventDefault()
        if (!formIsValid) {
            return
        }
        communicating.set(true)
        let res
        try {
            res = await setPassword($params['request-token'], $password)
        } catch (e) {
            res = e
        }
        communicating.set(false)
        if (!res.success) {
            if (res.error) error.set(res.error)
            else error.set($lg == 'fr' ? 'L’opération n’a pas pu être effectuée' : 'The operation could not be completed')
        } else {
            $success = true
        }
    }

    interface Suggestion {
        [key: string]: string
    }
    interface SugCond {
        ereg: RegExp
        text: Suggestion
    }
    const suggestions = writable([] as Suggestion[])

    const sugConditions: SugCond[] = [
        {
            ereg: /[^a-z]/g,
            text: { en: 'a few lowercase letters (a, b, … z)', fr: 'quelques lettres minuscules (a, b, … z)' },
        },
        {
            ereg: /[^A-Z]/g,
            text: { en: 'a few upper letters (A, B, … Z)', fr: 'quelques lettres majuscules (A, B, … Z)' },
        },
        {
            ereg: /[^0-9]/g,
            text: { en: 'a few numbers (0, 1, … 9)', fr: 'quelques nombres (0, 1, … 9)' },
        },
        {
            ereg: /[a-zA-Z0-9]/g,
            text: { en: 'a few symbols (“!”, “@”, “%”, “-”, spaces, accented letters, …)', fr: 'quelques symboles (“!”, “@”, “%”, “-”, espace, lettre avec accent, …)' },
        },
    ]

    // Handle password suggestions
    unsubs.push(
        password.subscribe((pw) => {
            const pwLen = pw.length
            const pwLenDiff = 16 - pwLen
            const sugs: Suggestion[] = []

            if (pwLenDiff > 1) {
                sugs.push({ fr: `${pwLenDiff} caractères à votre mot de passe`, en: `${pwLenDiff} characters to your password` })
            } else if (pwLenDiff == 1) {
                sugs.push({ fr: 'un caractère à votre mot de passe', en: 'one character to your password' })
            }

            for (const sug of sugConditions) {
                if (pw.replace(sug.ereg, '').length < 1) {
                    sugs.push(sug.text)
                }
            }

            suggestions.set(sugs)
        }),
    )
</script>

<form novalidate onsubmit={formSubmit}>
    <div class="logo-text">
        {$lg == 'fr' ? 'Nouveau mot de passe' : 'New password'}
    </div>

    {#if $success}
        <p class="gen-text one-line">
            {#if $lg == 'fr'}
                Votre nouveau mot de passe est maintenant en fonction.
            {:else}
                Your new password is now active.
            {/if}
        </p>
        <p><a href="/{$lg}/">{$lg == 'fr' ? 'Retourner à l’accueil.' : 'Go back to the home page.'}</a></p>
    {:else}
        <div class="inp-password">
            <label for="password" class="sr-only">{$lg == 'fr' ? 'Mot de passe' : 'Password'}</label>
            <div class="input-group">
                <span class="input-group-text"><Icon inline icon="fa:lock" /></span>
                <input class="form-control" id="password" type="password" bind:value={fields.password} disabled={$communicating} placeholder={$lg == 'fr' ? 'Mot de passe' : 'Password'} oninput={() => clearError()} class:is-valid={passwordIsValid} class:is-invalid={!passwordIsValid} />
            </div>
        </div>

        {#if $password.length < MIN_PASSWORD_LENGTH}
            <div class="password-sugs text-danger one-line">{$lg == 'fr' ? `Nous exigeons au moins ${MIN_PASSWORD_LENGTH} caractères.` : `We require at least ${MIN_PASSWORD_LENGTH} characters.`}</div>
        {:else if $suggestions.length}
            <div class="password-sugs">
                <p class="text-warning">{$lg == 'fr' ? 'Nous suggérons l’ajout de :' : 'We suggest adding:'}</p>
                <ul>
                    {#each $suggestions as suggestion, i}
                        <li class="text-warning">
                            {suggestion[$lg]}{#if i == $suggestions.length - 1}.{:else}
                                ;{/if}
                        </li>
                    {/each}
                </ul>
            </div>
        {/if}

        <div class="inp-password">
            <label for="pconfirm" class="sr-only">{$lg == 'fr' ? 'Confirmation du mot de passe' : 'Password confirmation'}</label>
            <div class="input-group">
                <span class="input-group-text"><Icon inline icon="fa:lock" /></span>
                <input class="form-control" id="pconfirm" type="password" bind:value={fields.pconfirm} disabled={$communicating} placeholder={$lg == 'fr' ? 'Confirmation du mot de passe' : 'Password confirmation'} oninput={() => clearError()} class:is-valid={pconfirmIsValid || fields.password == ''} class:is-invalid={!pconfirmIsValid && fields.password != ''} />
            </div>
        </div>

        {#if $error != null}
            <div class="error">
                <div class="alert alert-danger">{$error}</div>
            </div>
        {:else}
            <div class="inp-action d-grid">
                <button class="btn btn-primary" type="submit" disabled={!formIsValid || $communicating || $error != null}>
                    {#if $communicating}{$lg == 'fr' ? 'Veuillez patienter…' : 'Validating…'}
                    {:else}{$lg == 'fr' ? 'Enregistrer le mot de passe' : 'Set the new password'}
                    {/if}
                </button>
            </div>
        {/if}
    {/if}
</form>
